import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Calculate as CalculateIcon,
  Category as CategoryIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  ListAlt as ListAltIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import calculatorApi from '../services/calculatorApi';
import api from '../axiosConfig';
import PricelistGenerator from './PricelistGenerator';

const ServicesManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/solo') ? '/solo/calculator' : '/calculator';

  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [businessSettings, setBusinessSettings] = useState(null);
  const [tiers, setTiers] = useState([]);
  // tierPriceMap: { serviceId: [ { tier_id, tier_name, price, recommended_price, total_cost } ] }
  const [tierPriceMap, setTierPriceMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [calculating, setCalculating] = useState(false);
  const [pricelistOpen, setPricelistOpen] = useState(false);

  // Picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState('add');
  const [pickerTarget, setPickerTarget] = useState(null);
  const [salonServices, setSalonServices] = useState([]);
  const [salonServicesLoading, setSalonServicesLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerError, setPickerError] = useState(null);
  const [pickerSaving, setPickerSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [servicesData, categoriesData, settingsData, tiersData] = await Promise.all([
        calculatorApi.getServices(),
        calculatorApi.getServiceCategories(),
        calculatorApi.getBusinessSettings(),
        api.get('/calculator/tiers').then(r => r.data || []).catch(() => []),
      ]);
      setServices(servicesData);
      setCategories(categoriesData);
      setBusinessSettings(settingsData);
      setTiers(tiersData);

      // Build tier price map directly from the services response
      const map = {};
      servicesData.forEach(s => {
        map[s.id] = s.tier_prices || [];
      });
      setTierPriceMap(map);

    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load services. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Returns a display string for a service's tier prices e.g. "£8.10 – £16.54"
  const getTierPriceSummary = (serviceId) => {
    const prices = (tierPriceMap[serviceId] || [])
      .map(p => parseFloat(p.price))
      .filter(p => p > 0)
      .sort((a, b) => a - b);

    if (prices.length === 0) return null;
    if (prices.length === 1) return `£${prices[0].toFixed(2)}`;
    return `£${prices[0].toFixed(2)} – £${prices[prices.length - 1].toFixed(2)}`;
  };

  const isPriced = (serviceId) => {
    const prices = (tierPriceMap[serviceId] || [])
      .map(p => parseFloat(p.price))
      .filter(p => p > 0);
    return prices.length > 0;
  };

  // Returns true if any tier for this service is priced below its stored cost
  const hasAnyBelowCost = (serviceId) => {
    return (tierPriceMap[serviceId] || []).some(p => {
      const price = parseFloat(p.price) || 0;
      const cost  = parseFloat(p.total_cost) || 0;
      return price > 0 && cost > 0 && price < cost;
    });
  };

  // Returns true if any tier for this service is priced below its recommended price
  const hasAnyBelowRecommended = (serviceId) => {
    return (tierPriceMap[serviceId] || []).some(p => {
      const price = parseFloat(p.price) || 0;
      const rec   = parseFloat(p.recommended_price) || 0;
      return price > 0 && rec > 0 && price < rec;
    });
  };

  const openPicker = async (mode, calcService = null) => {
    setPickerMode(mode);
    setPickerTarget(calcService);
    setPickerSearch('');
    setPickerError(null);
    setPickerSaving(false);
    setPickerOpen(true);
    setSalonServicesLoading(true);
    try {
      const res = await api.get('/services');
      const allSalon = res.data || [];
      const linkedIds = new Set(
        services
          .filter(s => s.service_id && (!calcService || s.id !== calcService.id))
          .map(s => s.service_id)
      );
      setSalonServices(allSalon.filter(s => !linkedIds.has(s.id)));
    } catch (err) {
      setPickerError('Could not load services. Please try again.');
    } finally {
      setSalonServicesLoading(false);
    }
  };

  const handlePickerSelect = async (salonService) => {
    setPickerError(null);
    setPickerSaving(true);
    try {
      if (pickerMode === 'add') {
        const res = await api.post('/calculator/services', {
          service_id:    salonService.id,
          name:          salonService.name,
          duration:      salonService.duration,
          current_price: parseFloat(salonService.default_price || 0),
        });
        setServices(prev => [...prev, res.data]);
      } else {
        const res = await api.post(`/calculator/services/${pickerTarget.id}/link`, {
          service_id: salonService.id
        });
        setServices(prev => prev.map(s => s.id === pickerTarget.id ? res.data : s));
      }
      setPickerOpen(false);
      setPickerTarget(null);
    } catch (err) {
      setPickerError(err.response?.data?.error || 'Failed. Please try again.');
    } finally {
      setPickerSaving(false);
    }
  };

  const handleUnlink = async (calcService) => {
    if (!window.confirm(`Unlink "${calcService.name}" from its services entry? Price writeback will stop.`)) return;
    try {
      const res = await api.post(`/calculator/services/${calcService.id}/link`, { service_id: null });
      setServices(prev => prev.map(s => s.id === calcService.id ? res.data : s));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unlink service.');
    }
  };

  const handleEdit = (serviceId) => navigate(`${basePath}/services/${serviceId}`);

  const handleDuplicate = async (service) => {
    try {
      const newService = await calculatorApi.createService({
        ...service, name: `${service.name} (Copy)`, id: undefined, service_id: undefined
      });
      setServices([...services, newService]);
    } catch (err) {
      setError('Failed to duplicate service.');
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await calculatorApi.deleteService(serviceId);
      setServices(services.filter(s => s.id !== serviceId));
    } catch (err) {
      setError('Failed to delete service.');
    }
  };

  const handleCalculateAll = async () => {
    if (!window.confirm('Recalculate costs for all services? This may take a moment.')) return;
    setCalculating(true);
    setError(null);
    try {
      await Promise.all(
        services.map(s =>
          calculatorApi.calculateServiceCosts(s.id).catch(() => null)
        )
      );
      await loadData();
    } catch (err) {
      setError('Some services failed to calculate. Please try again.');
    } finally {
      setCalculating(false);
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Uncategorized';
  };

  const filteredServices = services.filter(service => {
    const matchesSearch =
      service.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      categoryFilter === 'all' || service.category_id === parseInt(categoryFilter);
    return matchesSearch && matchesCategory;
  });

  const filteredPickerServices = salonServices.filter(s =>
    s.name?.toLowerCase().includes(pickerSearch.toLowerCase())
  );

  const salonName = businessSettings?.salon_name || 'Our Services';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', py: 3 }}>
      <Container maxWidth="xl">

        {/* Header */}
        <Paper elevation={0} sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white', p: 3, mb: 3, borderRadius: 2
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 300, display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon /> Service Management
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Manage your salon services and pricing
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="contained" startIcon={<CategoryIcon />}
                onClick={() => navigate(`${basePath}/categories`)}
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' } }}>
                Categories
              </Button>
              <Button variant="contained" startIcon={<ListAltIcon />}
                onClick={() => setPricelistOpen(true)}
                disabled={services.length === 0}
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' } }}>
                Pricelist
              </Button>
              <Button variant="contained" startIcon={<AddIcon />}
                onClick={() => openPicker('add')}
                sx={{ backgroundColor: 'white', color: '#667eea', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                Add Service
              </Button>
            </Box>
          </Box>
        </Paper>

        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>{error}</Alert>}

        {tiers.length === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Before you can calculate prices, set your hourly rate in{' '}
            <Button size="small" onClick={() => navigate(`${basePath}/tiers`)}
              sx={{ p: 0, minWidth: 0, textTransform: 'none', verticalAlign: 'baseline' }}>
              Tier Management
            </Button>.
          </Alert>
        )}

        {/* Filters */}
        <Paper elevation={0} sx={{ mb: 3, p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small" sx={{ flexGrow: 1, minWidth: 200 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label="Category"
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined"
              startIcon={calculating ? <CircularProgress size={20} /> : <CalculateIcon />}
              onClick={handleCalculateAll} disabled={calculating}>
              {calculating ? 'Calculating...' : 'Calculate All'}
            </Button>
            <IconButton onClick={loadData} color="primary" aria-label="Refresh services"><RefreshIcon /></IconButton>
          </Box>
        </Paper>

        {/* Services Table */}
        <TableContainer component={Paper} elevation={0}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 600 }}>Service</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Materials</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Tier Prices</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Linked</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredServices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm || categoryFilter !== 'all'
                        ? 'No services match your filters'
                        : 'No services yet. Click "Add Service" to import from your services list.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredServices.map((service) => {
                  const isLinked    = !!service.service_id;
                  const priced      = isPriced(service.id);
                  const tierSummary = getTierPriceSummary(service.id);
                  const belowCost   = hasAnyBelowCost(service.id);
                  const belowRec    = !belowCost && hasAnyBelowRecommended(service.id);

                  // Build tooltip for tier breakdown
                  const tierTooltip = (tierPriceMap[service.id] || [])
                    .filter(p => parseFloat(p.price) > 0)
                    .map(p => {
                      const price = parseFloat(p.price).toFixed(2);
                      const rec   = p.recommended_price ? ` (rec: £${parseFloat(p.recommended_price).toFixed(2)})` : '';
                      return `${p.tier_name}: £${price}${rec}`;
                    })
                    .join(' · ');

                  return (
                    <TableRow key={service.id} sx={{
                      '&:hover': { backgroundColor: '#f8f9fa' },
                      '&:last-child td': { border: 0 }
                    }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{service.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getCategoryName(service.category_id)}
                        </Typography>
                        {service.description ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                            {service.description}
                          </Typography>
                        ) : null}
                      </TableCell>

                      <TableCell>
                        {service.duration
                          ? `${Math.floor(service.duration / 60)}h ${service.duration % 60}m`
                          : '-'}
                      </TableCell>

                      <TableCell>
                        £{(parseFloat(service.product_cost) || 0).toFixed(2)}
                      </TableCell>

                      {/* Tier Prices — with below-cost / below-recommended warning */}
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          {tierSummary ? (
                            <Tooltip title={tierTooltip || ''}>
                              <Typography variant="body2" sx={{
                                fontWeight: 500,
                                color: belowCost ? '#c62828' : belowRec ? '#BF360C' : '#2E7D32',
                                cursor: 'default'
                              }}>
                                {tierSummary}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" sx={{ color: '#757575' }}>Not priced</Typography>
                          )}

                          {belowCost && (
                            <Tooltip title="One or more tiers are priced below cost — edit to review">
                              <WarningIcon sx={{ fontSize: '1rem', color: '#c62828' }} />
                            </Tooltip>
                          )}
                          {belowRec && (
                            <Tooltip title="One or more tiers are below the recommended price">
                              <WarningIcon sx={{ fontSize: '1rem', color: '#BF360C' }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        {isLinked ? (
                          <Tooltip title="Linked — click to unlink">
                            <Chip icon={<CheckCircleIcon />} label="Linked" size="small"
                              color="success" variant="outlined"
                              onClick={() => handleUnlink(service)} sx={{ cursor: 'pointer' }} />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Not linked — click to link">
                            <Chip icon={<LinkIcon />} label="Link" size="small"
                              color="warning" variant="outlined"
                              onClick={() => openPicker('link', service)} sx={{ cursor: 'pointer' }} />
                          </Tooltip>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        <Chip
                          label={!priced ? 'Unpriced' : belowCost ? 'Below cost' : belowRec ? 'Review' : 'Priced'}
                          size="small"
                          color={!priced ? 'default' : belowCost ? 'error' : belowRec ? 'warning' : 'success'}
                        />
                      </TableCell>

                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Button size="small" variant="contained" color="primary"
                            onClick={() => handleEdit(service.id)} sx={{ minWidth: 'auto', px: 1 }}>
                            EDIT
                          </Button>
                          <Button size="small" variant="contained" color="inherit"
                            onClick={() => handleDuplicate(service)}
                            sx={{ minWidth: 'auto', px: 1, backgroundColor: '#757575' }}>
                            DUP
                          </Button>
                          <Button size="small" variant="contained" color="error"
                            onClick={() => handleDelete(service.id)} sx={{ minWidth: 'auto', px: 1 }}>
                            DEL
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Summary Stats */}
        <Paper elevation={0} sx={{ mt: 3, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 600 }}>{filteredServices.length}</Typography>
              <Typography variant="body2" color="text.secondary">Total Services</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 600 }}>
                {services.filter(s => s.service_id).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">Linked</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ color: '#BF360C', fontWeight: 600 }}>
                {services.filter(s => !s.service_id).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">Unlinked</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 600 }}>
                {services.filter(s => isPriced(s.id) && !hasAnyBelowCost(s.id)).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">Priced</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ color: '#BF360C', fontWeight: 600 }}>
                {services.filter(s => !isPriced(s.id)).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">Unpriced</Typography>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Pricelist Generator */}
      <PricelistGenerator
        open={pricelistOpen}
        onClose={() => setPricelistOpen(false)}
        services={services}
        categories={categories}
        salonName={salonName}
      />

      {/* Service Picker Dialog */}
      <Dialog open={pickerOpen} onClose={() => !pickerSaving && setPickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {pickerMode === 'add' ? 'Add service to calculator' : `Link "${pickerTarget?.name}" to a service`}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {pickerMode === 'add'
              ? 'Select a service from your services list. Only services not yet in the calculator are shown.'
              : 'Select the matching service. Prices will sync automatically when you save or calculate.'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {pickerError && <Alert severity="error" sx={{ mb: 2 }}>{pickerError}</Alert>}
          <TextField fullWidth placeholder="Search..." value={pickerSearch}
            onChange={(e) => setPickerSearch(e.target.value)}
            size="small" sx={{ mb: 2 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }}
          />
          {salonServicesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : filteredPickerServices.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {pickerSearch ? 'No services match your search' : 'All services are already in the calculator'}
            </Typography>
          ) : (
            <List dense>
              {filteredPickerServices.map(salonService => (
                <ListItemButton key={salonService.id}
                  onClick={() => handlePickerSelect(salonService)}
                  disabled={pickerSaving}
                  sx={{ borderRadius: 1, mb: 0.5, '&:hover': { backgroundColor: '#e8f5e9' } }}>
                  <ListItemText
                    primary={salonService.name}
                    secondary={`${salonService.duration} min · £${parseFloat(salonService.default_price || 0).toFixed(2)}`}
                  />
                  <ListItemSecondaryAction>
                    {pickerSaving
                      ? <CircularProgress size={16} />
                      : <Chip label="Select" size="small" color="primary" variant="outlined" />}
                  </ListItemSecondaryAction>
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)} disabled={pickerSaving}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServicesManagement;